import { forwardRef } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  VStack,
  Badge,
  useColorModeValue,
  Input,
} from '@chakra-ui/react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, isSameDay } from 'date-fns';

interface VacationDaysStepProps {
  selectedDates: Date[];
  onChange: (dates: Date[]) => void;
  startDate: Date;
  endDate: Date;
  workWeekDays: number[];
  onComplete: () => void;
  onBack: () => void;
}

// Custom Input component for Chakra UI integration with DatePicker
const CustomInput = forwardRef<HTMLInputElement, { value?: string; onClick?: () => void }>(
  ({ value, onClick }, ref) => (
    <Input
      value={value}
      onClick={onClick}
      ref={ref}
      readOnly
      borderColor={useColorModeValue('gray.200', 'gray.600')}
      _focus={{
        borderColor: useColorModeValue('blue.500', 'blue.300'),
        boxShadow: `0 0 0 1px ${useColorModeValue('blue.500', 'blue.300')}`,
      }}
    />
  )
);
CustomInput.displayName = 'CustomInput';

const VacationDaysStep = ({
  selectedDates,
  onChange,
  startDate,
  endDate,
  onComplete,
  onBack,
}: VacationDaysStepProps) => {
  // Custom date render - show vacation days in a different color
  const handleRemoveDate = (dateToRemove: Date) => {
    const updatedDates = selectedDates.filter(
      (date) => !isSameDay(date, dateToRemove)
    );
    onChange(updatedDates);
  };

  return (
    <Box>
      <Text mb={6}>
        Select the days you plan to take as vacation during your tracking period.
        These days will be excluded from your daily billable hour calculations.
      </Text>

      <Box mb={6}>
        <Text fontWeight="medium" mb={2}>
          Select Vacation Days
        </Text>
        <DatePicker
          selected={selectedDates.length > 0 ? selectedDates[selectedDates.length - 1] : new Date()}
          onChange={(date: Date | null) => {
            if (date) {
              // Add the date if it's not already selected
              if (!selectedDates.some(d => isSameDay(d, date))) {
                onChange([...selectedDates, date]);
              }
            }
          }}
          customInput={<CustomInput />}
          dateFormat="MM/dd/yyyy"
          minDate={startDate}
          maxDate={endDate}
          highlightDates={selectedDates}
          className="vacation-date-picker"
        />
      </Box>

      {selectedDates.length > 0 && (
        <Box mb={6}>
          <Text fontWeight="medium" mb={2}>
            Selected Vacation Days ({selectedDates.length})
          </Text>
          <VStack align="stretch" spacing={2} maxH="200px" overflowY="auto">
            {selectedDates
              .sort((a, b) => a.getTime() - b.getTime())
              .map((date, index) => (
                <HStack key={index} justify="space-between">
                  <Text>{format(date, 'MMMM d, yyyy')}</Text>
                  <Badge 
                    colorScheme="red" 
                    cursor="pointer" 
                    onClick={() => handleRemoveDate(date)}
                    px={2}
                    py={1}
                    borderRadius="full"
                  >
                    Remove
                  </Badge>
                </HStack>
              ))}
          </VStack>
        </Box>
      )}

      <Flex justify="space-between" mt={8}>
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button colorScheme="blue" onClick={onComplete}>
          Complete Setup
        </Button>
      </Flex>
    </Box>
  );
};

export default VacationDaysStep;